import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';

import { useMount } from '@simplywallst/ui-core';
import { CompanyNoteModalProvider } from '@components/Note';

import { OneColumn } from '@/components/Layout';
import DocumentHeader from '@/components/DocumentHeader';
import { useStream } from '@/components/Stream/hooks/useStream';

import { useWatchlist } from '@/hooks/useWatchlist';
import { useSelectedWatchlist } from '../../hooks/useSelectedWatchlist';

import { Error } from '../Empty';
import Header from '../Header';

import { WatchlistSVG } from './styled';

const Empty = loadable(() => import('../Empty'));
const PopulatedState = loadable(() => import('../PopulatedState'), {
  resolveComponent: (c) => c.PopulatedState,
});

const Watchlist = () => {
  useStream();
  const [, selectWatchlist] = useSelectedWatchlist();
  const { status, data: watchlist } = useWatchlist();

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  const watchlistItems = watchlist?.items ?? [];
  const isError =
    (status === 'error' && watchlistItems.length === 0) ||
    (status === 'success' && typeof watchlist === 'undefined');
  const isEmpty =
    status === 'success' && watchlist && watchlistItems.length === 0;

  const shouldShowTable =
    watchlistItems.length > 0 && watchlist && status !== 'error';

  if (isEmpty) {
    return (
      <Track>
        <DocumentHeader title="Watchlist" />
        <OneColumn>
          <Header watchlist={watchlist} onSelectWatchlist={selectWatchlist} />
          <WatchlistSVG />
          <Empty />
        </OneColumn>
      </Track>
    );
  }

  if (isError) {
    return (
      <Track>
        <DocumentHeader title="Watchlist" />
        <OneColumn>
          <WatchlistSVG />
          <Error data-cy-id="error-state">
            <h3>Something went wrong!</h3>
            <p>
              We&apos;re sorry, but an error occurred. Please try again later.
            </p>
          </Error>
        </OneColumn>
      </Track>
    );
  }

  if (shouldShowTable) {
    return (
      <Track>
        <OneColumn>
          <DocumentHeader title="Watchlist" />
          <CompanyNoteModalProvider>
            <PopulatedState watchlist={watchlist} />
          </CompanyNoteModalProvider>
        </OneColumn>
      </Track>
    );
  }

  return null;
};

export default Watchlist;
